import SelectCustomizable from 'core/modules/atoms/SelectCustomizable';
import DialogDefault from 'core/modules/components/dialogs/components/Dialog';
import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';

const LessonSelectionDialog = ({ t, open, onClose, onSelect, courses, units, loadingUnits, loadCourseUnits, contentError }) => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedCourse(null);
      setSelectedUnit(null);
      setSelectedLesson(null);
    }
  }, [open]);

  const courseOptions = useMemo(() => {
    if (!courses || courses.length === 0) return [];

    return courses.map((item) => ({
      value: item.guid,
      label: item.name || item.title,
      data: item,
    }));
  }, [courses]);

  const unitOptions = useMemo(() => {
    if (!selectedCourse || !units || units.length === 0) return [];

    return units.map((unit, index) => ({
      value: unit.section?.guid || unit.guid,
      label: decodeHtmlWithWhiteList(unit.section?.name || unit.section || `${t('chatbot:Unit')} ${index + 1}`),
      data: unit,
    }));
  }, [selectedCourse, units, t]);

  const lessonOptions = useMemo(() => {
    if (!selectedUnit || !selectedUnit.data) return [];

    const unitLessons = selectedUnit.data.items || [];

    return unitLessons.map((lesson, index) => ({
      value: lesson.guid,
      label: decodeHtmlWithWhiteList(lesson.lesson_name || `${t('chatbot:Lesson')} ${index + 1}`),
      data: lesson,
    }));
  }, [selectedUnit, t]);

  const handleCourseChange = (option) => {
    setSelectedCourse(option);
    setSelectedUnit(null);
    setSelectedLesson(null);

    if (option && option.value) {
      loadCourseUnits(option.value);
    }
  };

  const handleUnitChange = (option) => {
    setSelectedUnit(option);
    setSelectedLesson(null);
  };

  const handleLessonChange = (option) => {
    setSelectedLesson(option);
  };

  const handleConfirm = () => {
    if (selectedLesson && selectedCourse && selectedUnit) {
      // Don't set a default word count - it will be fetched after selection
      const wordCount = selectedLesson.data.wordCount || (selectedLesson.data.content ? selectedLesson.data.content.split(/\s+/).length : 0) || null;

      onSelect({
        guid: selectedLesson.value,
        title: selectedLesson.label,
        wordCount: wordCount,
        courseGuid: selectedCourse.value,
        courseName: selectedCourse.label,
        unitName: selectedUnit.label,
        lessonGuid: selectedLesson.value,
      });
    }
  };

  const isConfirmDisabled = !selectedLesson || !selectedCourse || !selectedUnit;

  return (
    <DialogDefault
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSuccess={handleConfirm}
      title={t('chatbot:Choose content from course')}
      buttonTextCancel={t('actions:Cancel')}
      buttonTextSucess={t('actions:Send')}
      disabledSuccess={isConfirmDisabled}
      size="small"
      className="lesson-selection-dialog"
    >
      <div className="lesson-selection-dialog__content">
        <div className="lesson-selection-dialog__field">
          <SelectCustomizable
            options={courseOptions}
            value={selectedCourse}
            onChange={handleCourseChange}
            placeholder={t('chatbot:Select subject')}
            isSearchable={true}
            isClearable={false}
          />
        </div>

        <div className="lesson-selection-dialog__field">
          <SelectCustomizable
            options={unitOptions}
            value={selectedUnit}
            onChange={handleUnitChange}
            placeholder={t('chatbot:Select unit')}
            isSearchable={false}
            isClearable={false}
            isDisabled={unitOptions.length === 0}
            error={
              selectedCourse && !loadingUnits && unitOptions.length === 0 ? (
                <div className="lesson-selection-dialog__error-title">{t('chatbot:teacher_tools_error_content_unavailable_title')}</div>
              ) : null
            }
          />
        </div>

        <div className="lesson-selection-dialog__field">
          <SelectCustomizable
            options={lessonOptions}
            value={selectedLesson}
            onChange={handleLessonChange}
            placeholder={t('chatbot:Select lesson')}
            isSearchable={true}
            isClearable={false}
            isDisabled={lessonOptions.length === 0}
            error={
              selectedUnit && !loadingUnits && lessonOptions.length === 0 ? (
                <div className="lesson-selection-dialog__error-title">{t('chatbot:teacher_tools_error_content_unavailable_title')}</div>
              ) : null
            }
          />
          {selectedLesson ? (
            <div className="lesson-selection-dialog__link">
              <a
                href={`/course/${selectedCourse?.value}${selectedLesson?.data?.lesson_type === 'mint' ? `/unit/${selectedUnit?.value}` : ''}/lesson/${
                  selectedLesson?.value
                }`}
                target="_blank"
                rel="noopener noreferrer"
                className="lesson-selection-dialog__link-text"
              >
                {t('chatbot:see_book_content')}
              </a>
            </div>
          ) : null}
        </div>

        {/* if either error is present, show the error */}
        {(contentError || (selectedCourse && !loadingUnits && unitOptions.length === 0) || (selectedUnit && !loadingUnits && lessonOptions.length === 0)) && (
          <div className="lesson-selection-dialog__error">
            <div className="lesson-selection-dialog__error-message">{t('chatbot:teacher_tools_error_content_unavailable_message')}</div>
          </div>
        )}
      </div>
    </DialogDefault>
  );
};

LessonSelectionDialog.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  courses: PropTypes.arrayOf(PropTypes.object).isRequired,
  units: PropTypes.arrayOf(PropTypes.object),
  loadingUnits: PropTypes.bool,
  loadCourseUnits: PropTypes.func.isRequired,
  contentError: PropTypes.string,
};

export default withTranslation(['chatbot', 'actions'])(LessonSelectionDialog);
